"use strict";
const STAFF_ASSISTANT_LAUNCH_ERROR = "Staff Assistant could not be opened. Try again.";
const STAFF_ASSISTANT_READY_TIMEOUT_MS = 15e3;
const PILL_PICKER_PLUGIN_ID = "pill-picker";
export const CASE_ACTIVITY_REFRESH_EVENT = "sta-case-activity-refresh";
const CASE_ASSISTANT_INSERTABLE_STATES = ["idle", "busy", "stalled"];
const CASE_ASSISTANT_SETTLE_CHECKS = 3;
const caseAssistantContexts = /* @__PURE__ */ new WeakMap();
let openStaffAssistantInFlight = null;
let staffAssistantReadyTimeoutMs = STAFF_ASSISTANT_READY_TIMEOUT_MS;
let caseActivityWatchers = /* @__PURE__ */ new WeakMap();
function settingsHaveError(settings) {
  if (settings == null || typeof settings !== "object") {
    return true;
  }
  return Object.prototype.hasOwnProperty.call(settings, "error");
}
export function appendCaseAssistantPillPickerPlugin(settings) {
  const record = settings;
  const existing = Array.isArray(record.plugins) ? record.plugins : [];
  const plugins = existing.includes(PILL_PICKER_PLUGIN_ID) ? existing : [...existing, PILL_PICKER_PLUGIN_ID];
  return {
    ...record,
    plugins
  };
}
function normalizeStaffAssistantError(_error) {
  return new Error(STAFF_ASSISTANT_LAUNCH_ERROR);
}
function findStaffAssistantToggle() {
  return document.getElementById("staff_assistant_bot");
}
function findKnowledgeBot() {
  return document.getElementById("eab-knowledge-bot-interface") ?? document.body.querySelector("eab-ioc-bot") ?? document.body.querySelector("eab-nav-knowledge-bot");
}
function nextReadyCheck() {
  return new Promise((resolve) => window.setTimeout(resolve, 25));
}
async function waitForCaseAssistantBot(trackSession) {
  const deadline = Date.now() + staffAssistantReadyTimeoutMs;
  let appendableSamples = 0;
  let timeoutId;
  const timedOut = new Promise((resolve) => {
    timeoutId = window.setTimeout(() => resolve(false), staffAssistantReadyTimeoutMs);
  });
  trackSession();
  const trackingInterval = window.setInterval(trackSession, 25);
  try {
    while (Date.now() < deadline) {
      const kbi = findKnowledgeBot();
      const updated = await Promise.race([
        Promise.resolve(kbi?.updateComplete).then(() => true),
        timedOut
      ]);
      if (!updated || Date.now() >= deadline) return null;
      const bot = kbi?.bot;
      const hasPillPicker = bot?.plugins?.get?.(PILL_PICKER_PLUGIN_ID);
      const canAppend = CASE_ASSISTANT_INSERTABLE_STATES.includes(bot?.state ?? "");
      if (kbi && canAppend && bot?.insertMessage && hasPillPicker) {
        appendableSamples += 1;
        if (appendableSamples >= CASE_ASSISTANT_SETTLE_CHECKS) {
          return kbi;
        }
      } else {
        appendableSamples = 0;
      }
      await Promise.race([nextReadyCheck(), timedOut]);
    }
    return null;
  } finally {
    window.clearTimeout(timeoutId);
    window.clearInterval(trackingInterval);
    trackSession();
  }
}
function transcriptEndsWithCaseAssistantMessage(bot, greeting, pillPickerRaw) {
  const messages = bot.messages?.readAll?.() ?? bot.messages?.messages ?? [];
  const pills = messages[messages.length - 1];
  const greetingMessage = messages[messages.length - 2];
  return pills?.type === PILL_PICKER_PLUGIN_ID && pills.raw === pillPickerRaw && greetingMessage?.type === "from-bot" && greetingMessage.raw?.trim() === greeting;
}
async function appendCaseAssistantMessage(kbi, greetingHtml, pillPicker) {
  const bot = kbi.bot;
  const greeting = greetingHtml.trim();
  const pillPickerRaw = JSON.stringify(pillPicker);
  if (transcriptEndsWithCaseAssistantMessage(bot, greeting, pillPickerRaw)) return;
  await bot.insertMessage(greeting, "from-bot");
  await bot.insertMessage(pillPickerRaw, PILL_PICKER_PLUGIN_ID);
  kbi.requestUpdate?.();
}
function watchForCaseActivityWrites(caseId) {
  const id = caseId.trim();
  if (!id) return;
  const bot = findKnowledgeBot()?.bot;
  if (!bot?.pointCut) return;
  const watchedCases = caseActivityWatchers.get(bot) ?? /* @__PURE__ */ new Set();
  if (watchedCases.has(id)) return;
  watchedCases.add(id);
  caseActivityWatchers.set(bot, watchedCases);
  bot.pointCut.add("after", "message-done", (message) => {
    window.dispatchEvent(
      new CustomEvent(CASE_ACTIVITY_REFRESH_EVENT, { detail: { caseId: id } })
    );
    return message;
  });
}
export function ensureStaffAssistantCaseContext(toggle, caseId, studentProfileNk) {
  const id = caseId.trim();
  const nk = studentProfileNk?.trim();
  if (!id) {
    return;
  }
  const currentEndpoint = toggle.dataEndpoint || toggle.getAttribute("data-endpoint");
  if (!currentEndpoint) {
    return;
  }
  const url = new URL(currentEndpoint, window.location.origin);
  url.searchParams.set("case_id", id);
  if (nk) {
    url.searchParams.set("student_profile_nk", nk);
  } else {
    url.searchParams.delete("student_profile_nk");
  }
  const nextEndpoint = `${url.pathname}${url.search}`;
  toggle.setAttribute("data-endpoint", nextEndpoint);
  toggle.dataEndpoint = nextEndpoint;
}
async function openStaffAssistantForCaseOnce(request) {
  const toggle = findStaffAssistantToggle();
  if (!toggle?.refetch || !toggle.showKBI) {
    throw new Error(STAFF_ASSISTANT_LAUNCH_ERROR);
  }
  ensureStaffAssistantCaseContext(toggle, request.caseId, request.studentProfileNk);
  const context = JSON.stringify([
    toggle.dataEndpoint || toggle.getAttribute("data-endpoint"),
    request.caseId.trim(),
    request.studentProfileNk?.trim() || ""
  ]);
  const existingBot = findKnowledgeBot();
  const existingContext = existingBot && caseAssistantContexts.get(existingBot);
  if (existingBot && (existingContext === void 0 || existingContext === context)) {
    toggle.showKBI({});
  } else {
    const settings = await toggle.refetch();
    if (settingsHaveError(settings)) {
      throw new Error(STAFF_ASSISTANT_LAUNCH_ERROR);
    }
    toggle.showKBI(appendCaseAssistantPillPickerPlugin(settings));
  }
  const kbi = await waitForCaseAssistantBot(() => {
    const mountedBot = findKnowledgeBot();
    if (!mountedBot) return;
    caseAssistantContexts.set(mountedBot, context);
    watchForCaseActivityWrites(request.caseId);
  });
  if (!kbi) {
    console.error("[CaseAssistant] Staff Assistant never became ready for the case greeting.");
    return;
  }
  await appendCaseAssistantMessage(kbi, request.greetingHtml, request.pillPicker);
}
export function openStaffAssistantForCase(request) {
  if (openStaffAssistantInFlight) {
    return openStaffAssistantInFlight;
  }
  openStaffAssistantInFlight = (async () => {
    try {
      await openStaffAssistantForCaseOnce(request);
    } catch (error) {
      throw normalizeStaffAssistantError(error);
    } finally {
      openStaffAssistantInFlight = null;
    }
  })();
  return openStaffAssistantInFlight;
}
export function __resetOpenStaffAssistantInFlightForTests() {
  openStaffAssistantInFlight = null;
}
export function __setStaffAssistantReadyTimeoutForTests(ms) {
  staffAssistantReadyTimeoutMs = ms ?? STAFF_ASSISTANT_READY_TIMEOUT_MS;
}
export function __resetCaseActivityWatchersForTests() {
  caseActivityWatchers = /* @__PURE__ */ new WeakMap();
}
export function clearKnowledgeBotSessionStorage() {
  Object.keys(sessionStorage).filter((key) => key.startsWith("kbi_")).forEach((key) => sessionStorage.removeItem(key));
}
const activeModuleStorageKey = "kbi_active_module";
function moduleFromPath(pathname) {
  if (pathname.startsWith("/pm") || pathname.startsWith("/portal")) return "rs";
  if (pathname.startsWith("/ss")) return "ss";
  if (pathname.split("/").includes("as")) return "adv";
  if (pathname.includes("/crm")) return "crm";
  return "ss";
}
function currentStaffBotModule(toggleEl) {
  const endpoint = toggleEl.getAttribute("data-endpoint");
  if (endpoint) {
    const moduleParam = new URL(endpoint, window.location.origin).searchParams.get("module");
    if (moduleParam) return moduleParam;
  }
  return moduleFromPath(window.location.pathname);
}
export function clearKnowledgeBotSessionStorageOnModuleSwitch(toggleEl) {
  const currentModule = currentStaffBotModule(toggleEl);
  const previousModule = sessionStorage.getItem(activeModuleStorageKey);
  if (previousModule && previousModule !== currentModule) {
    clearKnowledgeBotSessionStorage();
  }
  sessionStorage.setItem(activeModuleStorageKey, currentModule);
}
const defaultBrandClr = "#0d4781";
function brightness(hex) {
  let raw = (hex || "").replace("#", "");
  if (raw.length === 3) {
    raw = raw[0] + raw[0] + raw[1] + raw[1] + raw[2] + raw[2];
  }
  const red = parseInt(raw.substring(0, 2), 16) || 0;
  const green = parseInt(raw.substring(2, 4), 16) || 0;
  const blue = parseInt(raw.substring(4, 6), 16) || 0;
  return (red * 299 + green * 587 + blue * 114) / 1e3;
}
function resolveButtonIconColor(settings) {
  if (settings.button_icon_color) {
    return settings.button_icon_color;
  }
  const bg = settings.button_color ?? defaultBrandClr;
  return brightness(bg) > 128 ? "#000000" : "#ffffff";
}
function applyBranding(docStyle, settings) {
  docStyle.setProperty("--kb-param-user-text-background-color", settings.user_text_background_color);
  docStyle.setProperty("--kb-param-header-color", settings.header_color);
  docStyle.setProperty("--kb-param-button-color", settings.button_color ?? defaultBrandClr);
  docStyle.setProperty("--kb-param-button-icon-color", resolveButtonIconColor(settings));
}
function addStaffBotLinkListeners(kbiEl) {
  if (!kbiEl || !kbiEl.shadowRoot) return;
  const shadowRoot = kbiEl.shadowRoot;
  if (kbiEl.hasAttribute("data-staff-ai-delegated")) return;
  kbiEl.setAttribute("data-staff-ai-delegated", "true");
  shadowRoot.addEventListener("click", (e) => {
    const target = e.target;
    const link = target.closest?.('a[href*="?ai=1"]');
    if (!link) return;
    e.preventDefault();
    const messages = kbiEl.bot?.messages?.messages?.slice(2) || [];
    let conversationText = "";
    messages.forEach((obj) => {
      if (obj.raw) {
        conversationText += obj.type === "from-bot" ? "Chatbot: " : "Human: ";
        conversationText += obj.raw + "\n";
      }
    });
    localStorage["ai_staff_assistant_context"] = conversationText;
    const a = document.createElement("a");
    a.href = link.href;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  });
}
export function validateChatHistory(kbiEl) {
  void kbiEl.updateComplete?.then(async () => {
    const chatHistory = kbiEl.bot?.plugins?.list().find((p) => p.id === "n360-chat-history");
    if (chatHistory?.validate) {
      await chatHistory.validate(kbiEl);
    }
  });
}
function observeStaffBot() {
  const staffBotObserver = new MutationObserver(() => {
    const kbiEl = document.body.querySelector("eab-nav-knowledge-bot") ?? document.body.querySelector("eab-ioc-bot");
    if (kbiEl && kbiEl.bot) {
      staffBotObserver.disconnect();
      addStaffBotLinkListeners(kbiEl);
      void kbiEl.updateComplete?.then(() => kbiEl.updateComplete)?.then(() => validateChatHistory(kbiEl));
    }
  });
  staffBotObserver.observe(document.body, { childList: true, subtree: true });
}
const PROFILE_ENDPOINT_PARAMS = ["student_profile_nk", "prospect_nk", "contact_nk"];
export function profileContextParamsChanged(previousEndpoint, nextEndpoint) {
  if (!previousEndpoint) return false;
  const previous = new URL(previousEndpoint, window.location.origin).searchParams;
  const next = new URL(nextEndpoint, window.location.origin).searchParams;
  return PROFILE_ENDPOINT_PARAMS.some((key) => previous.get(key) !== next.get(key));
}
export function remountStaffBotIfProfileContextChanged(previousEndpoint, nextEndpoint) {
  if (!profileContextParamsChanged(previousEndpoint, nextEndpoint)) return;
  const bot = document.body.querySelector("eab-ioc-bot, eab-nav-knowledge-bot");
  if (!bot) return;
  bot.remove();
  observeStaffBot();
}
function profileContextFromHash(hash) {
  const prospectMatch = hash.match(/^#\/prospects\/(\d+)(?:[/?]|$)/);
  if (prospectMatch) {
    return { key: "prospect_nk", value: `prs${prospectMatch[1]}` };
  }
  const contactMatch = hash.match(/^#\/(?:pm|as|ss)\/contacts\/(\d+)(?:\/|$)/);
  if (contactMatch) {
    return { key: "contact_nk", value: `ctt${contactMatch[1]}` };
  }
  return null;
}
function profileContextFromPath(pathname) {
  const prospectMatch = pathname.match(/^\/(?:pm|as|ss)\/prospects\/(prs\d+)(?:\/|$)/i);
  if (prospectMatch) {
    return { key: "prospect_nk", value: prospectMatch[1].toLowerCase() };
  }
  const contactMatch = pathname.match(/^\/(?:pm|as|ss)\/contacts\/(ctt\d+)(?:\/|$)/i);
  if (contactMatch) {
    return { key: "contact_nk", value: contactMatch[1].toLowerCase() };
  }
  return null;
}
function applyStudentProfileNkFromToggle(url, toggleEl) {
  if (!toggleEl.hasAttribute("data-student-profile-nk")) return;
  url.searchParams.delete("student_profile_nk");
  const studentNk = toggleEl.getAttribute("data-student-profile-nk")?.trim();
  if (studentNk) {
    url.searchParams.set("student_profile_nk", studentNk);
  }
}
export function updateStaffBotProfileContext(toggleEl) {
  const endpoint = toggleEl.getAttribute("data-endpoint");
  if (!endpoint) return;
  const url = new URL(endpoint, window.location.origin);
  url.searchParams.delete("prospect_nk");
  url.searchParams.delete("contact_nk");
  applyStudentProfileNkFromToggle(url, toggleEl);
  const context = profileContextFromPath(window.location.pathname) || profileContextFromHash(window.location.hash);
  if (context) {
    url.searchParams.set(context.key, context.value);
  }
  const previousEndpoint = endpoint;
  const nextEndpoint = `${url.pathname}${url.search}`;
  toggleEl.setAttribute("data-endpoint", nextEndpoint);
  remountStaffBotIfProfileContextChanged(previousEndpoint, nextEndpoint);
}
export function initStaffBotSetup() {
  const toggleEl = document.getElementById("staff_assistant_bot");
  if (!toggleEl) return;
  clearKnowledgeBotSessionStorageOnModuleSwitch(toggleEl);
  updateStaffBotProfileContext(toggleEl);
  window.addEventListener("hashchange", () => updateStaffBotProfileContext(toggleEl));
  const brandFromAttr = toggleEl.getAttribute("data-branding-color")?.trim();
  const brand = brandFromAttr || defaultBrandClr;
  applyBranding(document.documentElement.style, {
    user_text_background_color: brand,
    header_color: brand,
    button_color: brand,
    button_icon_color: toggleEl.getAttribute("data-button-icon-color")
  });
  observeStaffBot();
}
